import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DocsCodeBlock } from '@/features/docs/components/DocsCodeBlock'

export interface CodeSnippet {
  label: string
  language: string
  code: string
}

interface CodeSnippetTabsProps {
  snippets: Array<CodeSnippet>
}

const FILE_NAME: Record<string, string> = {
  python: 'main.py',
  javascript: 'main.ts',
  bash: 'bash',
}

/**
 * Tabbed code-block chrome shared by the chat and generation model detail
 * pages — only the snippet content differs between them.
 */
export function CodeSnippetTabs({ snippets }: CodeSnippetTabsProps) {
  const [active, setActive] = useState(0)
  const [copied, setCopied] = useState(false)
  const current = snippets[active]

  const handleCopy = () => {
    navigator.clipboard.writeText(current.code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="overflow-hidden rounded-[1.25rem] border border-[var(--color-border)] bg-[var(--color-bg-primary)]">
      <div
        role="tablist"
        aria-label="Language"
        className="flex items-center gap-1 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]/60 px-2 py-2"
      >
        {snippets.map((snippet, i) => (
          <button
            key={snippet.label}
            type="button"
            role="tab"
            aria-selected={active === i}
            onClick={() => setActive(i)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-body-sm font-medium shadow-none! transition-colors duration-300 ease-brisk focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/50',
              active === i
                ? 'border-[var(--color-border-strong)] bg-[var(--color-control-surface-hover)] text-[var(--color-text-primary)]'
                : 'border-transparent text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            {snippet.label}
          </button>
        ))}
        <span className="ml-auto hidden font-mono text-caption text-[var(--color-text-tertiary)] sm:inline">
          {FILE_NAME[current.language] ?? current.language}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 font-mono text-caption text-[var(--color-text-tertiary)] shadow-none! transition-colors duration-300 ease-brisk hover:text-[var(--color-text-primary)]"
          title={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'copied' : 'copy'}
        </button>
      </div>
      <div className="[&_.docs-code-block]:rounded-none [&_.docs-code-block]:border-none [&_div]:!my-0">
        <DocsCodeBlock className={`language-${current.language}`}>
          {current.code}
        </DocsCodeBlock>
      </div>
    </div>
  )
}
